/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { Box, Text } from 'grommet';
import { NotaryStatus, NotaryStatusText } from 'blb-constants/ron';
import { MaterialIcon, PopoverList } from '@blend/enterprise-components';
import { Button } from '@blend/alchemy';
import styled from 'styled-components';

export const EditNotaryStatusTransitions: Partial<Record<NotaryStatus, NotaryStatus[]>> = {
  [NotaryStatus.ACTIVE]: [NotaryStatus.REJECTED, NotaryStatus.TERMINATED],
  [NotaryStatus.REJECTED]: [NotaryStatus.ACTIVE, NotaryStatus.TERMINATED],
  [NotaryStatus.TERMINATED]: [NotaryStatus.ACTIVE],
  [NotaryStatus.EXPIRED]: [NotaryStatus.ACTIVE, NotaryStatus.REJECTED, NotaryStatus.TERMINATED],
};

const StatusMenuItemText: Partial<Record<NotaryStatus, string>> = {
  [NotaryStatus.ACTIVE]: 'Approve notary',
  [NotaryStatus.REJECTED]: 'Reject notary',
  [NotaryStatus.TERMINATED]: 'Terminate notary',
};

export namespace NotaryStatusButton {
  export interface Props {
    onUpdateStatus: (newStatus: NotaryStatus) => void;
    status: NotaryStatus;
  }
}

const ButtonBody = styled(Box).attrs(() => ({
  align: 'center',
  pad: { horizontal: '12px', vertical: 'xxsmall' },
  width: { min: '80px' },
}))``;

export const NotaryStatusButton: React.FC<NotaryStatusButton.Props> = ({ status, onUpdateStatus }) => {
  const color = 'skyTone45';

  const renderStatusSelectionButtonGroup = () => {
    return (
      <Box border={{ color, size: 'xsmall' }} direction="row" id="it-notaryStatusButton" round="3px">
        <Button id={`it-statusBtn-ACTIVE`} onClick={() => onUpdateStatus(NotaryStatus.ACTIVE)}>
          <ButtonBody>
            <Text color={color}>Approve</Text>
          </ButtonBody>
        </Button>
        <Box
          border={[
            { color, side: 'left', size: 'xsmall' },
            { color, side: 'right', size: 'xsmall' },
          ]}
        >
          <Button id={`it-statusBtn-REJECTED`} onClick={() => onUpdateStatus(NotaryStatus.REJECTED)}>
            <ButtonBody>
              <Text color={color}>Reject</Text>
            </ButtonBody>
          </Button>
        </Box>
        <Box>
          <Button id={`it-statusBtn-TERMINATED`} onClick={() => onUpdateStatus(NotaryStatus.TERMINATED)}>
            <ButtonBody>
              <Text color={color}>Terminate</Text>
            </ButtonBody>
          </Button>
        </Box>
      </Box>
    );
  };

  const renderEditButton = (buttonStatuses: NotaryStatus[]) => {
    const menuItems = buttonStatuses.map(newStatus => {
      return {
        key: newStatus,
        label: StatusMenuItemText[newStatus],
        onClick: () => onUpdateStatus(newStatus),
      };
    });
    return (
      <Box id="it-notaryStatusButton">
        <PopoverList anchorPosition="bottom-end" buttonConfigs={menuItems} data-it={`it-statusBtn-${status}`}>
          <Box background={color} border={{ color, size: 'xsmall' }} round="3px">
            <Button>
              <ButtonBody direction="row" gap="12px">
                <Text color="white">{NotaryStatusText[status]}</Text>
                <MaterialIcon color="white" size={14} type="keyboard_arrow_down" />
              </ButtonBody>
            </Button>
          </Box>
        </PopoverList>
      </Box>
    );
  };

  const renderReadonlyStatus = () => (
    <Box border={{ color: 'cloud30', size: 'xsmall' }} direction="row" id="it-notaryStatusButton" round="3px">
      <ButtonBody background="cloud30" id={`it-statusBtn-${status}`}>
        <Text color="night60">{NotaryStatusText[status]}</Text>
      </ButtonBody>
    </Box>
  );

  const statusTransitions = EditNotaryStatusTransitions[status];
  if (statusTransitions) {
    return renderEditButton(statusTransitions);
  }

  if (status === NotaryStatus.UNDER_REVIEW || status === NotaryStatus.NEW) {
    return renderStatusSelectionButtonGroup();
  }

  return renderReadonlyStatus();
};
